import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useApiQuery } from "../hooks";
import type { Check } from "../types";

type Props = {
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (checkId: string | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  helperText?: string;
  error?: boolean;
  appConfigId?: string;
};

export const CheckSelect = ({
  label = "Select Check",
  placeholder = "Search and select an API check...",
  value,
  onChange,
  disabled = false,
  size = "medium",
  fullWidth = true,
  helperText,
  error = false,
  appConfigId,
}: Props) => {
  // Build the query path with app_config_id if provided
  const queryPath = useMemo(() => {
    if (!appConfigId) return "/checks";
    return `/checks?app_config_id=${appConfigId}`;
  }, [appConfigId]);

  // Handle both array and paginated responses
  const { data: rawData, isLoading: isLoadingChecks } = useApiQuery<
    Check[] | { checks: Check[]; total: number }
  >(queryPath);

  // Normalize the response to always be an array and filter to api-type checks
  const checks = useMemo((): Check[] => {
    if (!rawData) return [];
    const list = Array.isArray(rawData) ? rawData : (rawData.checks ?? []);
    return list.filter((check) => check.check_type === "api");
  }, [rawData]);

  const [searchQuery, setSearchQuery] = useState("");

  // Create Fuse instance for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(checks ?? [], {
      keys: ["name", "id"],
      threshold: 0.3,
      includeScore: true,
    });
  }, [checks]);

  // Filter checks based on search query
  const filteredChecks = useMemo(() => {
    if (checks.length === 0) return [];

    if (!searchQuery.trim()) {
      return checks;
    }

    return fuse
      .search(searchQuery, { limit: 20 })
      .map(({ item }) => item)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [checks, searchQuery, fuse]);

  // Find the currently selected check
  const selectedCheck = useMemo(() => {
    if (!value || checks.length === 0) return null;
    return checks.find((check) => check.id === value) || null;
  }, [value, checks]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || isLoadingChecks}
      loading={isLoadingChecks}
      options={filteredChecks}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      value={selectedCheck}
      onChange={(_, newValue, reason) => {
        onChange(newValue?.id || null);
        if (reason === "clear") {
          setSearchQuery("");
        }
      }}
      onInputChange={(_, newInputValue) => {
        setSearchQuery(newInputValue);
      }}
      inputValue={searchQuery}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input": {
              padding: "8px 12px",
              fontSize: "0.875rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
            "& .MuiFormHelperText-root": {
              fontSize: "0.75rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoadingChecks ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                fontSize: "0.875rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              }}
            >
              {option.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              }}
            >
              ID: {option.id}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={option.name} size="small" {...tagProps} />;
        })
      }
      noOptionsText={
        searchQuery ? "No checks found matching your search" : "No API checks available"
      }
      loadingText="Loading checks..."
      clearOnBlur={false}
      clearOnEscape={false}
      selectOnFocus={false}
      blurOnSelect={true}
      openOnFocus={true}
      filterOptions={(x) => x} // Disable built-in filtering since we use Fuse
      sx={{
        "& .MuiAutocomplete-input": {
          cursor: "pointer",
        },
      }}
    />
  );
};
